import { ROLE_LABELS } from 'core';
import {
  ChevronDown,
  KeyRound,
  Lock,
  LockOpen,
  LogOut,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { usePoints } from '@/lib/master-data';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { toast } from '@/components/ui/Toast';
import { useAuth } from '@/lib/auth';
import { countUnsent } from '@/lib/outbox';
import { cn } from '@/lib/utils';
import { PinDialog } from './PinDialog';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (
    ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toUpperCase() ||
    '?'
  );
}

/** User menu: identity, password change, device PIN, lock, logout. */
export function UserMenu({
  compact = false,
  className,
}: {
  compact?: boolean;
  className?: string;
}) {
  const { user, me, logout, lock, removePin } = useAuth();
  const navigate = useNavigate();
  const { data: points } = usePoints();
  const [pinOpen, setPinOpen] = useState(false);
  const [removePinOpen, setRemovePinOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [unsent, setUnsent] = useState(0);
  if (!user) return null;

  const pointName = user.pointId != null ? points?.find((p) => p.id === user.pointId)?.name : null;

  const askLogout = async () => {
    setUnsent(await countUnsent(user.id));
    setLogoutOpen(true);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(
            'flex h-11 min-w-11 items-center gap-2 rounded-full border border-border bg-background pl-1 pr-2 text-left text-sm hover:bg-accent',
            className,
          )}
          aria-label={`Menu utente: ${user.displayName}`}
        >
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
            {initials(user.displayName)}
          </span>
          {compact ? null : (
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block truncate font-medium">{user.displayName}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {ROLE_LABELS[user.role]}
              </span>
            </span>
          )}
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>
            <span className="flex items-center gap-2 font-semibold">
              <UserRound className="size-4" aria-hidden /> {user.displayName}
            </span>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              {user.username} · {ROLE_LABELS[user.role]}
              {pointName ? ` · ${pointName}` : ''}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => navigate('/cambio-password')}>
            <KeyRound /> Cambia password
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setPinOpen(true)}>
            <ShieldCheck />{' '}
            {me?.hasPin ? 'Cambia PIN del dispositivo' : 'Imposta PIN del dispositivo'}
          </DropdownMenuItem>
          {me?.hasPin ? (
            <>
              <DropdownMenuItem onSelect={() => setRemovePinOpen(true)}>
                <LockOpen /> Rimuovi PIN
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => lock()}>
                <Lock /> Blocca ora
              </DropdownMenuItem>
            </>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem destructive onSelect={() => void askLogout()}>
            <LogOut /> Esci
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <PinDialog open={pinOpen} onOpenChange={setPinOpen} />
      <ConfirmDialog
        open={removePinOpen}
        onOpenChange={setRemovePinOpen}
        title="Rimuovere il PIN?"
        description="Il dispositivo non si bloccherà più per inattività."
        confirmLabel="Rimuovi PIN"
        destructive
        onConfirm={async () => {
          await removePin();
          toast({ title: 'PIN rimosso', variant: 'success' });
        }}
      />
      <ConfirmDialog
        open={logoutOpen}
        onOpenChange={setLogoutOpen}
        title="Uscire dall’app?"
        description={
          unsent === 1
            ? 'Su questo dispositivo c’è 1 operazione non ancora accettata dal server: resta salvata e la ritroverai in Sincronizzazione al prossimo accesso con il tuo utente. Per inviarla subito, annulla e controlla la pagina Sincronizzazione.'
            : unsent > 1
              ? `Su questo dispositivo ci sono ${unsent} operazioni non ancora accettate dal server: restano salvate e le ritroverai in Sincronizzazione al prossimo accesso con il tuo utente. Per inviarle subito, annulla e controlla la pagina Sincronizzazione.`
              : 'Per rientrare serviranno nome utente e password.'
        }
        confirmLabel="Esci"
        destructive={unsent > 0}
        onConfirm={async () => {
          await logout();
          navigate('/login', { replace: true });
        }}
      />
    </>
  );
}
